<template>
  <div class="srq-form">
    <rmr-notice variant="info" title="Comment ça marche">
      Une demande concerne un seul site. Collez des URL de sites web, de flux
      RSS, de chaînes YouTube, de comptes X / Instagram / TikTok / LinkedIn ou
      de subreddits. Pour un site web, le flux RSS est utilisé s'il existe,
      sinon la page est suivie via RSS.app. Les URL déjà suivies, ou couvertes
      par un flux existant, sont refusées. La demande est traitée dès son
      envoi (sinon par le robot, toutes les 10 minutes).
      <div v-if="quota" class="srq-form__quota-info">
        <strong>Quota RSS.app en direct :</strong>
        <template v-if="quotaAvailable">
          {{ quota.used }} / {{ quota.max }} flux utilisés
          ({{ quota.remaining }} restants - {{ quota.percentage }}%)
        </template>
        <template v-else>indisponible pour le moment</template>
      </div>
    </rmr-notice>

    <div class="srq-form__grid">
      <div class="srq-form__field srq-form__field--site">
        <span class="rmr-table-shell__filter-label">Site</span>
        <rmr-autocomplete
          v-model="form.siteId"
          :items="siteList"
          item-text="name"
          item-value="id"
          label="Sélectionner un site"
          size="md"
          :disabled="submitting"
        />
      </div>

      <div class="srq-form__field">
        <span class="rmr-table-shell__filter-label">Support</span>
        <!-- Un contrôle local, et non `MediaTypeSegmented` : celui-ci écrit
             dans le store de la Veille globale et relance sa recherche, ce
             qui n'a aucun sens ici. Le support d'une URL YouTube / TikTok est
             de toute façon forcé en vidéo par le back. -->
        <rmr-segmented-control
          :value="form.mediaType"
          :items="mediaTypeItems"
          size="md"
          @input="form.mediaType = $event"
        />
      </div>
    </div>

    <div class="srq-form__field">
      <span class="rmr-table-shell__filter-label">
        URL à ajouter (une par ligne)
      </span>
      <rmr-textarea
        v-model="form.urlsText"
        size="md"
        rows="8"
        placeholder="https://www.example.com
https://www.youtube.com/@chaine
https://www.reddit.com/r/subreddit"
        :disabled="submitting"
      />
      <div class="srq-form__hint" :class="{ 'srq-form__hint--error': tooMany }">
        {{ urlCount }} URL{{ urlCount > 1 ? "s" : "" }} saisie{{
          urlCount > 1 ? "s" : ""
        }}
        (maximum {{ maxUrls }} par demande)
      </div>
    </div>

    <div class="srq-form__actions">
      <rmr-action-button
        variant="ghost"
        :icon="icons.mdiFileExcelOutline"
        label="Importer un Excel"
        :disabled="submitting"
        @click="triggerFileInput"
      />
      <input
        ref="fileInput"
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        @change="handleFileImport"
      />
      <span v-if="importedFileName" class="srq-form__file">
        {{ importedFileName }}
      </span>
      <v-spacer />
      <rmr-action-button
        :icon="icons.mdiSend"
        label="Envoyer la demande"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="submit"
      />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import { mdiFileExcelOutline, mdiSend } from "@mdi/js";
import * as XLSX from "xlsx";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrTextarea from "@/components/ui/forms/RmrTextarea.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

// Même borne que `MAX_URLS_PER_REQUEST` côté API : au-delà, le back répond
// 422 ; on préfère griser le bouton et le dire.
const MAX_URLS = 500;

// Une cellule Excel « ressemble à une URL » si elle n'a pas d'espace et
// contient un TLD. Les en-têtes et titres de colonnes sont ainsi ignorés sans
// qu'on ait à deviner la structure du fichier. La validation stricte reste au
// back (`invalid_url`), qui garde une trace de ce qu'il a refusé.
const looksLikeUrl = (value) => /^\S+\.[a-z]{2,}(\/\S*)?$/i.test(value);

export default {
  name: "SourceRequestForm",
  components: {
    RmrActionButton,
    RmrAutocomplete,
    RmrNotice,
    RmrSegmentedControl,
    RmrTextarea,
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPost } = useAxios();
    const { store } = useProxy();

    const form = reactive({
      siteId: null,
      mediaType: "written",
      urlsText: "",
    });
    const submitting = ref(false);
    const fileInput = ref(null);
    const importedFileName = ref("");
    const quota = ref(null);

    const loadQuota = async () => {
      try {
        const { data } = await axiosGet("/news-radar-source-requests/quota");
        quota.value = data;
      } catch (err) {
        console.warn("Could not load RSS.app quota", err);
      }
    };

    // =>  COMPUTED
    // L'API renvoie des compteurs `null` (et `error`) si RSS.app est
    // injoignable : ne jamais afficher un faux « 0 utilisé ».
    const quotaAvailable = computed(
      () =>
        !!quota.value &&
        !quota.value.error &&
        Number.isFinite(quota.value.used) &&
        Number.isFinite(quota.value.max)
    );
    const siteList = computed(
      () => store.getters["common/getAccessibleSiteList"] || []
    );

    const urls = computed(() =>
      form.urlsText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
    );
    const urlCount = computed(() => urls.value.length);
    const tooMany = computed(() => urlCount.value > MAX_URLS);
    const canSubmit = computed(
      () =>
        Boolean(form.siteId) &&
        urlCount.value > 0 &&
        !tooMany.value &&
        !submitting.value
    );

    const mediaTypeItems = [
      { value: "written", label: "Écrit" },
      { value: "video", label: "Vidéo" },
    ];

    // =>  METHODS
    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    // Lecture côté client, comme `UrlBatch.vue` : toutes les cellules de la
    // première feuille, à plat. Les URL importées s'AJOUTENT à la saisie.
    const handleFileImport = (event) => {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(new Uint8Array(e.target.result), {
            type: "array",
          });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const cells = XLSX.utils
            .sheet_to_json(worksheet, { header: 1 })
            .flat()
            .map((cell) => String(cell ?? "").trim())
            .filter((cell) => cell && looksLikeUrl(cell));

          if (!cells.length) {
            emit("error", "Aucune URL trouvée dans ce fichier");
            return;
          }
          importedFileName.value = `${file.name} (${cells.length} URL)`;
          form.urlsText = [...urls.value, ...cells].join("\n");
        } catch (error) {
          console.error("Error parsing Excel file:", error);
          emit("error", "Erreur lors de la lecture du fichier Excel");
        }
      };
      reader.readAsArrayBuffer(file);
    };

    const submit = async () => {
      if (!canSubmit.value) return;
      submitting.value = true;
      try {
        const { data } = await axiosPost("/news-radar-source-requests/", {
          site_id: form.siteId,
          media_type: form.mediaType,
          urls: urls.value,
        });
        form.urlsText = "";
        importedFileName.value = "";
        emit("created", data);
      } catch (error) {
        const detail = error?.response?.data?.detail;
        emit(
          "error",
          typeof detail === "string"
            ? detail
            : "La demande n'a pas pu être enregistrée"
        );
      } finally {
        submitting.value = false;
      }
    };

    // =>  HOOKS
    onMounted(async () => {
      loadQuota();
      await store.dispatch("common/getSiteList");
      // Un user restreint à un seul site n'a rien à choisir.
      if (!form.siteId && siteList.value.length === 1) {
        form.siteId = siteList.value[0].id;
      }
    });

    // =>  DATA
    return {
      canSubmit,
      fileInput,
      form,
      handleFileImport,
      icons: { mdiFileExcelOutline, mdiSend },
      importedFileName,
      loadQuota,
      maxUrls: MAX_URLS,
      mediaTypeItems,
      quota,
      quotaAvailable,
      siteList,
      submit,
      submitting,
      tooMany,
      triggerFileInput,
      urlCount,
    };
  },
};
</script>

<style lang="scss" scoped>
.srq-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.srq-form__grid {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.srq-form__field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.srq-form__field--site {
  flex: 1 1 320px;
  max-width: 480px;
}

.srq-form__hint {
  margin-top: 6px;
  font-size: $rmr-fs-base;
  color: $rmr-text-muted;
}

.srq-form__hint--error {
  color: $rmr-danger;
  font-weight: 700;
}

.srq-form__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.srq-form__file {
  font-size: $rmr-fs-base;
  color: $rmr-text-muted;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.srq-form__quota-info {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: $rmr-fs-sm;
  color: $rmr-text-primary;
}
</style>
